import { useLocation } from "wouter";
import { useAuth } from "@/hooks/useAuth";
import { useTheme } from "next-themes";
import { Search, Bell, HelpCircle, Sun, Moon, Monitor, ChevronRight, LogOut } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import UserAvatar from "@/components/common/UserAvatar";
import { menuGroups, canAccessItem } from "@/config/navigation";
import {
  Drawer,
  DrawerContent,
  DrawerTitle,
} from "@/components/ui/drawer";
import { useQuery } from "@tanstack/react-query";
import { Project, ProjectAssignment } from "@shared/schema";

interface MoreMenuProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function MoreMenu({ open, onOpenChange }: MoreMenuProps) {
  const { user, isAdmin, isTeamHead, logout } = useAuth();
  const [location, navigate] = useLocation();
  const { theme, setTheme } = useTheme();

  const handleNavigate = (path: string) => {
    navigate(path);
    onOpenChange(false);
  };

  const isItemActive = (itemPath: string) => {
    if (itemPath === "/") return location === "/";
    return location === itemPath || location.startsWith(itemPath + "/");
  };

  const cycleTheme = () => {
    if (theme === "light") setTheme("dark");
    else if (theme === "dark") setTheme("system");
    else setTheme("light");
  };

  const ThemeIcon = theme === "dark" ? Moon : theme === "system" ? Monitor : Sun;
  const themeLabel = theme === "dark" ? "Dark" : theme === "system" ? "Auto" : "Light";

  return (
    <Drawer open={open} onOpenChange={onOpenChange}>
      <DrawerContent className="max-h-[85vh]">
        <DrawerTitle className="sr-only">Navigation Menu</DrawerTitle>

        <div className="overflow-y-auto pb-[env(safe-area-inset-bottom)]">
          {/* User Profile Section */}
          <div className="bg-muted/50 rounded-lg p-3 mx-4 mt-2 flex items-center gap-3">
            <UserAvatar
              name={user?.name || "User"}
              src={user?.photoUrl ?? undefined}
              size="md"
            />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-muted-foreground truncate">{user?.title}</p>
              {isAdmin ? (
                <Badge variant="outline" className="text-[10px] mt-0.5 bg-purple-50 text-purple-700 dark:text-purple-300 border-purple-200 dark:bg-purple-950/30 dark:text-purple-400 dark:border-purple-800">
                  Admin
                </Badge>
              ) : isTeamHead ? (
                <Badge variant="outline" className="text-[10px] mt-0.5 bg-blue-50 text-blue-700 dark:text-blue-300 border-blue-200 dark:bg-blue-950/30 dark:text-blue-400 dark:border-blue-800">
                  Team Head
                </Badge>
              ) : (
                <span className="text-[10px] text-muted-foreground">Staff</span>
              )}
            </div>
          </div>

          {/* Quick Actions Row */}
          <div className="flex justify-around px-4 py-3">
            <button
              onClick={() => handleNavigate("/search")}
              className="flex flex-col items-center gap-1"
            >
              <span className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Search className="h-5 w-5 text-foreground" />
              </span>
              <span className="text-[10px] text-muted-foreground">Search</span>
            </button>
            <button
              onClick={() => handleNavigate("/notifications")}
              className="flex flex-col items-center gap-1"
            >
              <span className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <Bell className="h-5 w-5 text-foreground" />
              </span>
              <span className="text-[10px] text-muted-foreground">Alerts</span>
            </button>
            <button
              onClick={cycleTheme}
              className="flex flex-col items-center gap-1"
            >
              <span className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <ThemeIcon className="h-5 w-5 text-foreground" />
              </span>
              <span className="text-[10px] text-muted-foreground">{themeLabel}</span>
            </button>
            <button
              onClick={() => {
                // Help is typically a panel trigger, just close for now
                onOpenChange(false);
              }}
              className="flex flex-col items-center gap-1"
            >
              <span className="w-12 h-12 rounded-full bg-muted flex items-center justify-center">
                <HelpCircle className="h-5 w-5 text-foreground" />
              </span>
              <span className="text-[10px] text-muted-foreground">Help</span>
            </button>
          </div>

          {/* Navigation Groups */}
          <div className="border-t border-border">
            {menuGroups.map((group) => {
              const visibleItems = group.items.filter((item) =>
                canAccessItem(item, isAdmin, isTeamHead)
              );
              if (visibleItems.length === 0) return null;

              const groupKey = group.title.toLowerCase().replace(/\s+/g, "-").replace(/&/g, "");

              return (
                <div key={groupKey}>
                  <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 pt-3 pb-1">
                    {group.title}
                  </p>
                  {visibleItems.map((item) => {
                    const active = isItemActive(item.path);
                    return (
                      <button
                        key={item.path}
                        onClick={() => handleNavigate(item.path)}
                        className={`flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors ${
                          active
                            ? "bg-primary/10 text-primary"
                            : "text-foreground hover:bg-accent"
                        }`}
                      >
                        {item.icon}
                        <span className="flex-1 text-left">{item.label}</span>
                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>

          {/* My Projects Section */}
          <div className="border-t border-border px-4 pt-3 pb-2">
            <div className="flex items-center justify-between mb-2">
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                My Projects
              </p>
              <button
                className="text-xs text-primary hover:text-primary/80"
                onClick={() => handleNavigate("/projects")}
              >
                View All
              </button>
            </div>
            <MoreMenuProjects userId={user?.id} onNavigate={handleNavigate} />
          </div>

          {/* Logout Button */}
          <div className="border-t border-border px-4 py-3 mb-2">
            <button
              onClick={() => {
                logout();
                onOpenChange(false);
              }}
              className="flex items-center gap-3 w-full px-3 py-3 text-sm font-medium text-destructive hover:bg-destructive/10 rounded-lg transition-colors"
            >
              <LogOut className="h-5 w-5" />
              <span>Log Out</span>
            </button>
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}

function MoreMenuProjects({
  userId,
  onNavigate,
}: {
  userId?: number;
  onNavigate: (path: string) => void;
}) {
  const { data: assignments } = useQuery<ProjectAssignment[]>({
    queryKey: userId ? [`/api/project-assignments/user/${userId}`] : [],
    enabled: !!userId,
  });

  const { data: projects } = useQuery<Project[]>({
    queryKey: ["/api/projects"],
  });

  let userProjects = projects;
  if (assignments?.length) {
    userProjects = projects?.filter((project) =>
      assignments.some((a) => a.projectId === project.id)
    );
  } else if (projects?.length) {
    const active = projects.filter((p) => p.status === ("active" as string));
    if (active.length >= 3) {
      userProjects = active.slice(0, 3);
    } else {
      const others = projects.filter((p) => p.status !== ("active" as string)).slice(0, 3 - active.length);
      userProjects = [...active, ...others];
    }
  }

  if (!userProjects || userProjects.length === 0) {
    return <p className="text-xs text-muted-foreground py-2">No assigned projects</p>;
  }

  return (
    <div className="space-y-1">
      {userProjects.slice(0, 3).map((project) => (
        <button
          key={project.id}
          onClick={() => onNavigate(`/projects/${project.id}`)}
          className="flex items-center justify-between w-full px-2 py-2 text-sm rounded-md hover:bg-accent transition-colors"
        >
          <span className="truncate font-medium">{project.name}</span>
          <Badge variant="outline" className="text-[10px] ml-2 shrink-0">
            {project.status.charAt(0).toUpperCase() + project.status.slice(1)}
          </Badge>
        </button>
      ))}
    </div>
  );
}
